import TextIconInput from 'components/input/TextIconInput';
import React from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CheckboxInput from 'components/input/CheckboxInput';
import { useFormik } from 'formik';
import VerifyCodeInput from 'components/input/VerifyCodeInput';
import SelectPhoneCodeInput from 'components/input/SelectPhoneCodeInput';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { checkVerificationCode, sendVerificationCode } from 'app/authorization/authorizationSlice';
import * as Yup from 'yup';
import { Typography } from '@mui/material';
import { USER_ROLE } from 'constants/common';
import { useHistory } from 'react-router';
import { getUserId, setIsConfirmedByPhone } from 'utils/sessionStorage';

interface Props {
  setStep: (value: string) => void;
  currentStep: string;
  formik: any;
}

/** Digits only — strips spaces/dashes/parens so retries after a bad entry don't stick on the old regex. */
const normalizePhoneDigits = (value: unknown) => String(value ?? '').replace(/\D/g, '');

const resolveOnboardingStudentId = (reduxStudentId?: number) => {
  if (reduxStudentId && reduxStudentId > 0) {
    return reduxStudentId;
  }
  return Number(getUserId() || sessionStorage.getItem('session_student_id') || 0);
};

const isValidPhoneDigits = (value: unknown) => {
  const digits = normalizePhoneDigits(value);
  return digits.length >= 10 && digits.length <= 15;
};

const FormAuthPhoneNumber = (props: Props) => {
  const dispatch = useAppDispatch();
  const authorizationStore = useAppSelector((store) => store.authorization);
  let history = useHistory();
  const params = new URLSearchParams(history.location.search);
  const stepURL = params.get('step');
  const [stepAuth, setStepAuth] = React.useState<'phone' | 'code'>('phone');
  const [verifyCode, setVerifyCode] = React.useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const formik = useFormik({
    initialValues: {
      phone_number: '',
      country_code: '+1',
      receive_sms: false,
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      phone_number: Yup.string()
        .required('Phone number is required')
        .test('phone-digits', 'Phone number is not valid', isValidPhoneDigits),
    }),
    onSubmit: async (values) => {
      setSubmitError(null);
      const phoneNumber = normalizePhoneDigits(values.phone_number);
      const countryCode = values.country_code || '+1';
      // Keep national number + country_code separate for duplicate checks; E.164 for Twilio is built on BE.
      const studentId = resolveOnboardingStudentId(authorizationStore?.studentId);
      if (!studentId) {
        setSubmitError('Your session expired. Please log in again.');
        return;
      }
      const res = await dispatch(
        sendVerificationCode({
          student_id: studentId,
          country_code: countryCode,
          phone_number: phoneNumber,
        })
      );
      if (res.meta?.requestStatus === 'fulfilled' && res.payload) {
        const payload = res.payload as { status?: number; code?: number; message?: string; sid?: string };
        // Twilio failures were previously returned as HTTP 200 error objects (no sid).
        if (payload?.sid) {
          setStepAuth('code');
          return;
        }
        if (payload?.message) {
          setSubmitError(payload.message);
          return;
        }
        setStepAuth('code');
      } else if (res.meta?.requestStatus === 'rejected' && res.payload) {
        const err = res.payload as { message?: string | string[] };
        const message = Array.isArray(err?.message) ? err.message[0] : err?.message;
        setSubmitError(message || 'Something went wrong. Please try again.');
      }
    },
  });

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubmitError(null);
    // Allow common formatting while typing; validate/submit on digits only.
    formik.setFieldValue('phone_number', e.target.value ?? '', true);
  };

  const sendVerifyCode = async () => {
    try {
      const studentId = resolveOnboardingStudentId(authorizationStore?.studentId);
      if (!studentId) {
        setSubmitError('Your session expired. Please log in again.');
        return;
      }
      const res = await dispatch(
        checkVerificationCode({
          verification_code: verifyCode,
          student_id: studentId,
          country_code: formik.values.country_code,
          phone_number: normalizePhoneDigits(formik.values.phone_number),
          is_allow_notification: formik.values.receive_sms,
        })
      );
      if (res.payload) {
        if (res.payload.statusCode === 200) {
          setIsConfirmedByPhone(true);
          props.setStep('4');
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const [timer, setTimer] = React.useState(60);
  const interValRef: any = React.useRef();

  React.useEffect(() => {
    if (stepAuth === 'code')
      interValRef.current = setInterval(() => {
        setTimer((timer) => timer - 1);
      }, 1000);
    return () => {
      clearInterval(interValRef.current);
    };
  }, [stepAuth]);
  return (
    <div className="min-h-screen flex items-center justify-center p-0 mb-5 px-md-5 container">
      <div className="w-full max-w-lg mx-auto">
      <Form onSubmit={formik.handleSubmit}>
        {stepAuth === 'phone' && (
          <>
            <div className="m-3">
            {!stepURL && <Button type="button" className="text-field-light" variant="light" onClick={() => {
              const userRole = sessionStorage.getItem('userRole');
              if (userRole === USER_ROLE.student) {
                props.setStep('3a');
              } else if (userRole === USER_ROLE.parent) {
                props.setStep('3b');
              } else if (userRole === USER_ROLE.counselor) {
                props.setStep('3c');
              }
            }}>
                Back
              </Button>}
            </div>
            <h2 className="m-3">Confirm your phone number</h2>
            <div className="m-3 d-flex justify-content-between align-items-center">
              <h5>
                We will send you a SMS containing 4 digits for implementing the verification.
              </h5>
              {/* <Badge className="px-2 py-1" bg="secondary">
                <span className="h6">{props.currentStep}/2</span>
              </Badge> */}
            </div>
            <Row className="m-3 p-0">
              <SelectPhoneCodeInput
                // mdCol={'6'}
                values={formik.values.country_code}
                onChange={formik.handleChange}
                name={'country_code'}
                label={'Country code'}
              />

              <TextIconInput
                className="phone-number-input"
                mdCol={'6'}
                icon={<PhoneAndroidIcon />}
                label={'Phone number'}
                name={'phone_number'}
                values={formik.values.phone_number}
                onChange={handlePhoneChange}
                onBlur={formik.handleBlur}
                feedback={
                  submitError ? (
                    <span className="text-danger">{submitError}</span>
                  ) : formik.touched.phone_number && formik.errors.phone_number ? (
                    <span className="text-danger">{formik.errors.phone_number}</span>
                  ) : undefined
                }
              />
            </Row>
            {/* <div className="mx-3">
              <CheckboxInput
                mdCol={'6'}
                label={'I would like to receive SMS updates'}
                name={'receive_sms'}
                values={formik.values.receive_sms}
                onChange={formik.handleChange}
              />
            </div> */}
             <div className="mx-3">
              <CheckboxInput
                mdCol={'6'}
                label={
                  <span>I agree to receive SMS update messages from GradMap. Message frequency may vary. Reply STOP to unsubscribe.</span>
                }
                name={'receive_sms'}
                values={formik.values.receive_sms}
                onChange={formik.handleChange}
              />
              <div className="d-block mt-2 font-weight-bold mx-3">
                      <a
                        className="p-0 ml-4"
                        href="https://www.gradmap.com/privacy-policy/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        Privacy Policy
                      </a>
                      {' | '}
                      <a
                        className="p-0"
                        href="https://www.gradmap.com/terms-of-use/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        Terms of Use
                      </a>
            </div>
            </div>
            <div className="mx-3">
              <Button
                className="w-100 p-2 my-3"
                variant="primary"
                type="submit"
                disabled={formik.isSubmitting}
              >
                <span className="h5 fw-bold">Send Code</span>
              </Button>
            </div>
          </>
        )}
        {stepAuth === 'code' && (
          <>
            <div className="m-3">
              <Button
                className="text-field-light"
                variant="light"
                type="button"
                onClick={() => {
                  setStepAuth('phone');
                  setVerifyCode(undefined);
                  setSubmitError(null);
                  setTimer(60);
                }}
              >
                Back
              </Button>
            </div>
            <h2 className="m-3">Confirmation</h2>
            <div className="m-3 d-flex justify-content-between align-items-center">
              <h5>Please type the verification code we sent to your phone number</h5>
              {/* <Badge className="px-2 py-1" bg="secondary">
                <span className="h6">{props.currentStep}/2</span>
              </Badge> */}
            </div>
            <Col md={6} className="p-0">
              <div className="my-3 d-flex flex-column align-items-right">
                <Row className="p-2">
                  <TextIconInput
                    disabled
                    mdCol={'12'}
                    icon={<PhoneAndroidIcon />}
                    defaultValue={`${formik.values.country_code}${normalizePhoneDigits(formik.values.phone_number)}`}
                  />
                </Row>

                <div style={{ padding: '1rem' }}>
                  <VerifyCodeInput onChange={(value) => setVerifyCode(value.join(''))} />
                </div>
                {timer <= 0 ? (
                  <Button
                    type="button"
                    variant="outline-primary"
                    className="p-2 m-3"
                    onClick={() => {
                      setTimer(60);
                      formik.handleSubmit();
                    }}
                  >
                    Resend Code
                  </Button>
                ) : (
                  <Typography className="text-field-light w-100 text-center my-3">
                    {timer}s
                  </Typography>
                )}
                <Button type="button" className="p-2 m-3" variant="primary" onClick={() => sendVerifyCode()}>
                  <span className="h5 fw-bold">Verify Code</span>
                </Button>
              </div>
            </Col>
          </>
        )}
      </Form>
      </div>
    </div>
  );
};

export default FormAuthPhoneNumber;
